import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { TableColumn } from '../../interface/table-column.interface';

export interface ColumnMatchingDialogData {
    selectedColumns: TableColumn[];
    oppositeColumns: TableColumn[];
    sourceSide: 'source1' | 'source2';
}

export interface ColumnMatch {
    column: TableColumn;
    matched: boolean;
    similarityScore: number;
}

@Component({
    selector: 'app-column-matching-dialog',
    templateUrl: './column-matching-dialog.component.html',
    styleUrls: ['./column-matching-dialog.component.scss'],
    standalone: false,
})
export class ColumnMatchingDialogComponent implements OnInit {
    selectedColumns: TableColumn[];
    oppositeColumns: TableColumn[];
    sourceSide: 'source1' | 'source2';
    potentialMatches: ColumnMatch[] = [];
    selectedMatches: string[] = [];

    constructor(public dialogRef: MatDialogRef<any>, @Inject(MAT_DIALOG_DATA) public data: ColumnMatchingDialogData) {
        this.selectedColumns = data.selectedColumns;
        this.oppositeColumns = data.oppositeColumns;
        this.sourceSide = data.sourceSide;
    }

    ngOnInit(): void {
        this.findPotentialMatches();
    }

    private findPotentialMatches(): void {
        this.potentialMatches = this.oppositeColumns
            .map(column => {
                // Calculate max similarity across all selected columns
                let maxSimilarityScore = 0;
                this.selectedColumns.forEach(selectedColumn => {
                    const similarityScore = this.calculateSimilarity(selectedColumn, column);
                    if (similarityScore > maxSimilarityScore) {
                        maxSimilarityScore = similarityScore;
                    }
                });
                return {
                    column,
                    matched: maxSimilarityScore > 0.5, // Auto-match if similarity > 50%
                    similarityScore: maxSimilarityScore,
                };
            })
            .sort((a, b) => b.similarityScore - a.similarityScore);

        // Pre-select matched columns
        this.selectedMatches = this.potentialMatches.filter(match => match.matched).map(match => match.column.id);
    }

    private calculateSimilarity(column1: TableColumn, column2: TableColumn): number {
        let score = 0;

        // Name similarity (case-insensitive)
        const name1 = (column1.name || '').toLowerCase().trim();
        const name2 = (column2.name || '').toLowerCase().trim();

        if (name1 === name2) {
            score += 0.7; // Exact name match
        } else if (name1.includes(name2) || name2.includes(name1)) {
            score += 0.4; // Partial name match
        }

        // Data type match
        if (column1.inputType === column2.inputType) {
            score += 0.2;
        }

        // ID similarity (for technical columns)
        const id1 = (column1.id || '').toLowerCase();
        const id2 = (column2.id || '').toLowerCase();
        if (id1 === id2) {
            score += 0.1;
        }

        return Math.min(score, 1);
    }

    onMatchToggle(match: ColumnMatch): void {
        const index = this.selectedMatches.indexOf(match.column.id);
        if (index > -1) {
            this.selectedMatches.splice(index, 1);
            match.matched = false;
        } else {
            this.selectedMatches.push(match.column.id);
            match.matched = true;
        }
    }

    onConfirm(): void {
        const matchedColumns = this.potentialMatches
            .filter(match => this.selectedMatches.includes(match.column.id))
            .map(match => match.column);

        this.dialogRef.close(matchedColumns);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    getSimilarityPercentage(score: number): string {
        return Math.round(score * 100) + '%';
    }
}
